<script setup lang="ts">
import { ref, onMounted } from 'vue'
import {
  fontList,
  popularFonts,
  loadFonts,
  DEFAULT_OPEN_SANS_FONT_FOR_VERSION_2,
} from '../../fonts/font-helper'
import { debounce } from 'lodash'
import type { Editor } from '@tiptap/vue-3'
import {
  SearchLgIcon,
  FileQuestion01Icon,
  CheckIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UITextMdMedium, UIInput } from '@gohighlevel/ghl-ui'
import { LoadingIcon } from '../../assets'

// Props and emits
const props = defineProps({
  identifier: {
    type: Object,
    default: () => ({
      toolbarSelectFontFamily: 'toolbar_select--font_family',
    }),
  },
  locale: {
    type: Object,
    default: () => ({
      placeholders: {
        fontFamily: 'Font Family',
      },
    }),
  },
  selectedFontFamily: {
    type: String,
    default: '',
  },
  disableControl: {
    type: Boolean,
    default: false,
  },
  className: {
    type: String,
    default: '',
  },
  to: {
    type: String,
    default: undefined,
  },
  editor: {
    type: Object as () => Editor,
    required: true,
  },
  fallbackFont: {
    type: String,
    default: DEFAULT_OPEN_SANS_FONT_FOR_VERSION_2,
  },
  loadedFonts: {
    type: Array as () => string[],
    default: () => [],
  },
  fontsToLoad: {
    type: Array as () => string[],
    default: () => [],
  },
})

const emit = defineEmits(['update:fontFamily', 'fontsLoaded'])
const fontMapList = ref<Map<number, string>>(new Map())
const itemsPerPage = 10
const fontOptions = ref<{ label: string; value: string }[]>([])
const endIndex = ref(0)
const loading = ref(false)
const searchQuery = ref('')

const onScroll = (e: Event) => {
  const target = e.target as HTMLElement
  if (!target) return

  const isNearBottom =
    target.scrollTop + target.clientHeight >= target.scrollHeight - 50

  debounce(() => {
    if (isNearBottom && endIndex.value < fontMapList.value.size) {
      loadFontsPaginated(endIndex.value, itemsPerPage, searchQuery.value)
    }
  }, 300)()
}

const loadFontsPaginated = (
  initialIndex = 0,
  itemPerPage = 10,
  searchQuery = ''
) => {
  loading.value = true
  endIndex.value = initialIndex + itemPerPage
  if (searchQuery) {
    // Filter the font from the fontMapList based on the search query
    const filteredFontOptions = Array.from(fontMapList.value.values()).filter(
      font => font.toLowerCase().includes(searchQuery.toLowerCase())
    )

    // Map the filtered fonts to the correct format
    const filteredFontsMapped = filteredFontOptions.map(font => ({
      label: font,
      value: font,
    }))

    // Implement pagination for search results
    const startIdx = initialIndex
    const endIdx = Math.min(startIdx + itemPerPage, filteredFontsMapped.length)

    // Get the paginated results
    const paginatedResults = filteredFontsMapped.slice(startIdx, endIdx)

    // If this is the initial load (index 0), reset fontOptions, otherwise append
    if (initialIndex === 0) {
      fontOptions.value = paginatedResults
    } else {
      fontOptions.value = [...fontOptions.value, ...paginatedResults]
    }

    // Update endIndex to track pagination progress
    endIndex.value = endIdx

    // Load the fonts that aren't already loaded
    const fontsLoad = paginatedResults
      .map(opt => opt.value)
      .filter(font => !props.loadedFonts.includes(font))

    if (fontsLoad.length > 0) {
      loadFonts(fontsLoad)
      emit('fontsLoaded', fontsLoad)
    }
  } else {
    for (let i = initialIndex; i < endIndex.value; i++) {
      const font = fontMapList.value.get(i)
      if (font) {
        fontOptions.value.push({ label: font, value: font })
        // check loadedFonts array if the font is already loaded, no need to load
        if (!props.loadedFonts.includes(font)) {
          loadFonts([font])
          // sent the event to the parent component to notify about the newly loaded fonts
          emit('fontsLoaded', [font])
        }
      }
    }
  }
  loading.value = false
}

const generateFontMapList = () => {
  const uniqueFontList = [...props.fontsToLoad, ...popularFonts, ...fontList]
  const uniqueFonts = Array.from(new Set(uniqueFontList))
  uniqueFonts.forEach((font, index) => {
    fontMapList.value.set(index, font)
  })
}

const onSearch = (val: string) => {
  if (!val || val === '') {
    fontOptions.value = []
  }
  searchQuery.value = val
  loadFontsPaginated(0, itemsPerPage, searchQuery.value)
}

const onFontSelect = (font: string) => {
  if (props.editor && props.editor.commands) {
    if (font) {
      props.editor.chain().focus().setFontFamily(font).run()
    } else {
      props.editor.chain().focus().unsetFontFamily().run()
    }
  }
  emit('update:fontFamily', font)
}

onMounted(() => {
  generateFontMapList()
  loadFontsPaginated(0, itemsPerPage, searchQuery.value)
})
</script>

<template>
  <div
    class="w-64 h-auto max-h-80 bg-white rounded-lg border border-solid border-gray-200 drop-shadow overflow-hidden font-family-custom-dropdown px-2"
  >
    <div class="px-3 pt-2.5 mt-2">
      <div
        class="relative w-full h-10 flex flex-row justify-start items-center"
      >
        <UIInput
          :placeholder="$t('common.selectFont')"
          id="select-font-input-dropdown"
          class="font-family-dropdown-input w-32 m-2"
          type="text"
          :loading="loading"
          :clearable="true"
          size="medium"
          :value="searchQuery"
          @update:value="onSearch"
          :autofocus="true"
        >
          <template #prefix>
            <SearchLgIcon class="w-4 h-4 text-gray-500" />
          </template>
        </UIInput>

        <div v-if="loading" class="absolute z-10 right-0">
          <LoadingIcon />
        </div>
      </div>
    </div>

    <div class="border-b border-solid border-gray-200 pt-2"></div>

    <div class="w-full">
      <div
        v-if="fontOptions.length === 0"
        class="h-60 flex flex-col justify-center items-center pb-4"
      >
        <FileQuestion01Icon class="w-8 h-8" />
        <span class="mt-2 inter-forced">{{
          $t('validations.warnings.fontNotFound')
        }}</span>
      </div>
      <div v-else class="h-32 px-2">
        <ul
          class="list-none p-0 m-0 overflow-y-auto pr-1"
          @scroll="onScroll"
          style="scrollbar-width: thin"
        >
          <li
            class="w-full h-10 flex flex-row justify-start items-center hover:bg-gray-100 cursor-pointer pl-2"
            v-for="font in fontOptions"
            @click="onFontSelect(font.value)"
            :key="font.value"
          >
            <UITextMdMedium
              class="truncate text-sm text-gray-700 w-full ml-2 pl-2"
              :style="{ 'font-family': `${font.value} !important` }"
              >{{ font.label }}</UITextMdMedium
            >
            <span class="text-xs text-gray-500">
              <CheckIcon
                class="w-4 text-emerald-500 mr-2"
                v-if="font.value === selectedFontFamily"
              />
            </span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
